import type { AuditEntry, AuditSubjectGroup } from '@lms/shared-types'
import { apiRequestPaged, type PagedResult } from './client'

export interface ListAuditQuery {
  actorEmployeeId?: string
  action?: string
  /** One event family (kebab-case), e.g. `leave-application`, `holiday`. */
  category?: string
  /** Name search — matches actor/subject entity names (employee, team, …). */
  q?: string
  subjectType?: string
  subjectId?: string
  source?: string
  page?: number
  limit?: number
  /** Cursor: ISO datetime — return rows with created_at < this value. */
  before?: string | null
}

function buildQuery(query: ListAuditQuery): string {
  // The api route params are snake_case (FastAPI uses the Python param name
  // as the query key). Sending camelCase here silently no-ops the filter.
  const params = new URLSearchParams()
  if (query.actorEmployeeId) params.set('actor_employee_id', query.actorEmployeeId)
  if (query.action) params.set('action', query.action)
  if (query.category) params.set('category', query.category)
  if (query.q) params.set('q', query.q)
  if (query.subjectType) params.set('subject_type', query.subjectType)
  if (query.subjectId) params.set('subject_id', query.subjectId)
  if (query.source) params.set('source', query.source)
  if (query.before) params.set('before', query.before)
  if (query.page != null) params.set('page', String(query.page))
  if (query.limit != null) params.set('limit', String(query.limit))
  return params.toString()
}

/** Paged audit-log listing using cursor pagination (?before=<ISO datetime>).
 *  When `query.before` is set, uses cursor path; otherwise offset path. */
export function listAuditPage(
  query: ListAuditQuery = {},
): Promise<PagedResult<AuditEntry>> {
  const qs = buildQuery(query)
  return apiRequestPaged<AuditEntry>(`/api/v1/audit${qs ? `?${qs}` : ''}`)
}

/** Grouped-by-object audit view — one entry per object, newest change first.
 *  Cursor-paginated on `lastChangedAt` via `?before=`. */
export function listAuditSubjectGroups(
  query: Pick<
    ListAuditQuery,
    'category' | 'q' | 'subjectType' | 'source' | 'limit' | 'page' | 'before'
  > = {},
): Promise<PagedResult<AuditSubjectGroup>> {
  const qs = buildQuery(query)
  return apiRequestPaged<AuditSubjectGroup>(`/api/v1/audit/subjects${qs ? `?${qs}` : ''}`)
}
